// Fortnite stats service, backed by Fortnite-API.com
// Documentation: https://dash.fortnite-api.com/endpoints/stats
//
// Stats endpoints require an API key (free at https://dash.fortnite-api.com),
// passed as a raw `Authorization` header — no `Bearer` prefix.

const FORTNITE_API_BASE = 'https://fortnite-api.com/v2/stats/br/v2';

// Epic is the primary account type; psn/xbl let console players be looked up
// by their platform handle instead of an Epic display name.
export const FORTNITE_ACCOUNT_TYPES = ['epic', 'psn', 'xbl'] as const;
export type FortniteAccountType = (typeof FORTNITE_ACCOUNT_TYPES)[number];

export const FORTNITE_TIME_WINDOWS = ['lifetime', 'season'] as const;
export type FortniteTimeWindow = (typeof FORTNITE_TIME_WINDOWS)[number];

export interface FortniteAccount {
  id: string;
  name: string;
}

export interface FortniteBattlePass {
  level: number;
  progress: number | null;
}

// Every game mode reports the same shape. `ltm` and the per-mode splits are
// absent when the player has never played them, hence the optional fields.
export interface FortniteGameModeStats {
  score: number;
  scorePerMin: number;
  scorePerMatch: number;
  wins: number;
  top3?: number;
  top5?: number;
  top6?: number;
  top10?: number;
  top12?: number;
  top25?: number;
  kills: number;
  killsPerMin: number;
  killsPerMatch: number;
  deaths: number;
  kd: number;
  matches: number;
  winRate: number;
  minutesPlayed: number;
  playersOutlived: number;
  lastModified?: string;
}

export interface FortniteInputStats {
  overall: FortniteGameModeStats;
  solo?: FortniteGameModeStats;
  duo?: FortniteGameModeStats;
  trio?: FortniteGameModeStats;
  squad?: FortniteGameModeStats;
  ltm?: FortniteGameModeStats;
}

// A player who only ever used one input device has nulls for the others.
export interface FortniteStatsByInput {
  all: FortniteInputStats | null;
  keyboardMouse: FortniteInputStats | null;
  gamepad: FortniteInputStats | null;
  touch: FortniteInputStats | null;
}

export interface FortnitePlayerStats {
  account: FortniteAccount;
  battlePass: FortniteBattlePass;
  image: string | null;
  stats: FortniteStatsByInput;
}

interface FortniteApiEnvelope<T> {
  status: number;
  data?: T;
  error?: string;
}

export type FortniteFetchResult =
  | { ok: true; data: FortnitePlayerStats }
  | { ok: false; status: number; error: string };

export interface FortniteStatsQuery {
  name?: string;
  accountId?: string;
  accountType?: FortniteAccountType;
  timeWindow?: FortniteTimeWindow;
  /** Ask the upstream API to render a shareable stat-card PNG. */
  image?: boolean;
}

// Upstream returns its own status code inside the JSON envelope as well as on
// the response, and the two can disagree. The envelope is the reliable one.
function normalizeError(status: number, error?: string): { status: number; error: string } {
  if (status === 403) {
    return {
      status: 403,
      error: 'This account has public match history disabled in Fortnite settings.',
    };
  }
  if (status === 404) {
    // Upstream reuses 404 for "no such account" and for a real account that has
    // never finished a match. The latter reads as a bug if shown as "not found".
    if (error && /didn'?t play any match/i.test(error)) {
      return { status: 404, error: 'This account has no Battle Royale matches yet.' };
    }
    return { status: 404, error: 'Player not found' };
  }
  if (status === 401) {
    // A bad key is our misconfiguration, not the caller's bad request.
    return { status: 500, error: 'Fortnite API key is invalid or missing' };
  }
  return { status: status >= 400 ? status : 502, error: error || `Upstream returned ${status}` };
}

export async function fetchFortniteStats(
  apiKey: string,
  query: FortniteStatsQuery
): Promise<FortniteFetchResult> {
  const { name, accountId, accountType = 'epic', timeWindow = 'lifetime', image } = query;

  if (!name && !accountId) {
    return { ok: false, status: 400, error: 'name or accountId is required' };
  }

  const params = new URLSearchParams({ timeWindow });
  if (image) params.set('image', 'all');

  // Looking up by account id is the stable path — display names change, ids don't.
  let url: string;
  if (accountId) {
    url = `${FORTNITE_API_BASE}/${encodeURIComponent(accountId)}?${params.toString()}`;
  } else {
    params.set('name', name!);
    params.set('accountType', accountType);
    url = `${FORTNITE_API_BASE}?${params.toString()}`;
  }

  try {
    const response = await fetch(url, {
      headers: { Authorization: apiKey },
      next: { revalidate: 300 },
    });

    const body = (await response.json().catch(() => null)) as FortniteApiEnvelope<FortnitePlayerStats> | null;

    if (!response.ok || !body?.data) {
      const { status, error } = normalizeError(body?.status ?? response.status, body?.error);
      return { ok: false, status, error };
    }

    return { ok: true, data: body.data };
  } catch (error) {
    return {
      ok: false,
      status: 502,
      error: error instanceof Error ? error.message : 'Failed to reach Fortnite API',
    };
  }
}

// Stored player ids look like "DisplayName" or "DisplayName@psn". Console
// players need the account type to disambiguate their platform handle.
export function parseFortnitePlayerId(playerId: string): {
  name: string;
  accountType: FortniteAccountType;
} {
  const [name, rawType] = playerId.split('@');
  const candidate = (rawType || 'epic').toLowerCase();
  const accountType = (FORTNITE_ACCOUNT_TYPES as readonly string[]).includes(candidate)
    ? (candidate as FortniteAccountType)
    : 'epic';

  return { name: name.trim(), accountType };
}

// Prefer real input-device stats over the `all` rollup so a controller player's
// numbers aren't diluted, but fall back to whatever the account actually has.
export function getPrimaryInputStats(stats: FortniteStatsByInput): FortniteInputStats | null {
  const candidates = [stats.all, stats.keyboardMouse, stats.gamepad, stats.touch];
  return candidates.find((entry) => entry && entry.overall.matches > 0) ?? null;
}

export function formatWinRate(winRate: number): string {
  return `${winRate.toFixed(1)}%`;
}

// Upstream reports kd: 0 both for "never got a kill" and for a flawless record
// with no deaths. Rendering the latter as "0.00" understates the best players,
// so callers get null and should show kills instead.
export function formatKd(stats: Pick<FortniteGameModeStats, 'kd' | 'deaths'>): string | null {
  if (stats.deaths === 0) return null;
  return stats.kd.toFixed(2);
}

export function pluralizeMatches(matches: number): string {
  return `${matches.toLocaleString()} ${matches === 1 ? 'match' : 'matches'}`;
}

export function formatPlaytime(minutesPlayed: number): string {
  const hours = Math.round(minutesPlayed / 60);
  if (hours >= 1000) return `${(hours / 1000).toFixed(1)}k hrs`;
  return `${hours} hrs`;
}
